/**
 * What a record needs before it is fit to publish.
 *
 * Deliberately separate from the Zod schemas. Those decide what the API will
 * *accept* — an editor must always be able to save a half-finished draft and
 * come back to it. These rules decide what is fit to be seen by customers, and
 * they only ever warn.
 *
 * Kept here rather than in the admin UI so the editing screens and the publish
 * screen cannot drift into disagreeing about what "ready" means.
 */
import { LOCALES } from './i18n.js';
const hasText = (value, locale) => Boolean(value?.[locale]?.trim());
const hasAsset = (asset) => Boolean(asset?.key);
/**
 * Missing translations are reported per locale, but only for locales other than
 * English — English is what everything falls back to, so a missing English value
 * is a `required` issue reported by the caller instead.
 */
function translationIssues(field, label, value, { onlyIfEnglishPresent = true } = {}) {
    if (onlyIfEnglishPresent && !hasText(value, 'en'))
        return [];
    return LOCALES.filter((l) => l !== 'en' && !hasText(value, l)).map((locale) => ({
        field,
        label,
        severity: 'recommended',
        locale,
    }));
}
export function checkBrand(brand) {
    const issues = [];
    if (!hasText(brand.name, 'en')) {
        issues.push({ field: 'name', label: 'Brand name', severity: 'required', locale: 'en' });
    }
    // A brand tile with no logo is the one thing the app cannot render around.
    if (!hasAsset(brand.logo)) {
        issues.push({ field: 'logo', label: 'Logo', severity: 'required' });
    }
    if (!hasText(brand.description, 'en')) {
        issues.push({ field: 'description', label: 'Description', severity: 'recommended', locale: 'en' });
    }
    if (!hasAsset(brand.coverImage)) {
        issues.push({ field: 'coverImage', label: 'Cover image', severity: 'recommended' });
    }
    issues.push(...translationIssues('name', 'Brand name', brand.name));
    issues.push(...translationIssues('description', 'Description', brand.description));
    return issues;
}
export function checkModel(model) {
    const issues = [];
    if (!hasText(model.name, 'en')) {
        issues.push({ field: 'name', label: 'Model name', severity: 'required', locale: 'en' });
    }
    if (!hasAsset(model.tacticalImage)) {
        issues.push({ field: 'tacticalImage', label: 'Tactical image', severity: 'required' });
    }
    if (!hasText(model.description, 'en')) {
        issues.push({ field: 'description', label: 'Description', severity: 'required', locale: 'en' });
    }
    // The app groups and filters models by this, so a blank one drops out of
    // every category listing.
    if (!model.marketing?.category?.trim()) {
        issues.push({ field: 'marketing.category', label: 'Category', severity: 'required' });
    }
    if (!hasText(model.intro, 'en')) {
        issues.push({ field: 'intro', label: 'Intro', severity: 'recommended', locale: 'en' });
    }
    if ((model.usps ?? []).length === 0) {
        issues.push({ field: 'usps', label: 'At least one USP', severity: 'recommended' });
    }
    issues.push(...translationIssues('name', 'Model name', model.name));
    issues.push(...translationIssues('intro', 'Intro', model.intro));
    issues.push(...translationIssues('description', 'Description', model.description));
    issues.push(...checkColors(model.colors));
    return issues;
}
export function checkVariant(variant) {
    const issues = [];
    if (!hasText(variant.name, 'en')) {
        issues.push({ field: 'name', label: 'Variant name', severity: 'required', locale: 'en' });
    }
    // A trim exists to describe how it differs; with no highlights the app shows
    // an empty panel.
    if ((variant.usps ?? []).length === 0) {
        issues.push({ field: 'usps', label: 'At least one USP', severity: 'recommended' });
    }
    issues.push(...translationIssues('name', 'Variant name', variant.name));
    issues.push(...translationIssues('description', 'Description', variant.description));
    return issues;
}
function checkColors(colors) {
    const issues = [];
    (colors ?? []).forEach((color, index) => {
        const where = color.name.en?.trim() || `Colour ${index + 1}`;
        if (!hasText(color.name, 'en')) {
            issues.push({
                field: `colors.${index}.name`,
                label: `${where}: name`,
                severity: 'required',
                locale: 'en',
            });
        }
        if (!hasAsset(color.exteriorImage)) {
            issues.push({
                field: `colors.${index}.exteriorImage`,
                label: `${where}: exterior image`,
                severity: 'recommended',
            });
        }
    });
    return issues;
}
export function summarise(issues) {
    const required = issues.filter((i) => i.severity === 'required');
    return {
        issues,
        required,
        recommended: issues.filter((i) => i.severity === 'recommended'),
        readyToPublish: required.length === 0,
    };
}
/** One-line rendering of an issue, used in warnings and lists. */
export function describeIssue(issue) {
    const localeName = issue.locale === 'ar' ? 'Arabic' : issue.locale === 'en' ? 'English' : '';
    return localeName ? `${issue.label} (${localeName})` : issue.label;
}
//# sourceMappingURL=completeness.js.map